import { useState } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { ErrorEntitiesContent } from './components/error-entities-content';
import { EntityRelatedProps, UploadContent } from './components/upload-content';
import { useImportEntitiesStore } from './state';

export interface ImportEntitiesModalProps<TError> {
  entityRelatedProps: EntityRelatedProps<TError>;
  importStringResource: string;
  getErrorEntitiesColumns: () => ColumnDef<TError, any>[];
}

export function ImportEntitiesModal<TError>({
  entityRelatedProps,
  getErrorEntitiesColumns,
  importStringResource = 'authenticated.onboarding.team:import',
}: ImportEntitiesModalProps<TError>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { previewErrorOpen, resetState } = useImportEntitiesStore();

  const onCloseModal = () => {
    setOpen(false);
    resetState();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={value => {
        setOpen(value);
        resetState();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" size="sm" className="w-auto">
          <Download />
          {t(importStringResource)}
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] flex-col sm:max-w-2xl',
          previewErrorOpen ? 'gap-0' : 'gap-4'
        )}
      >
        {previewErrorOpen ? (
          <ErrorEntitiesContent
            getErrorEntitiesColumns={getErrorEntitiesColumns}
            backToImportResource={entityRelatedProps.res.backToImport}
          />
        ) : (
          <UploadContent onClose={onCloseModal} entityRelatedProps={entityRelatedProps} />
        )}
      </DialogContent>
    </Dialog>
  );
}
